import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';

function ReservationManagement() {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active, completed, cancelled, all
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    customerName: '',
    customerContact: '',
    customerEmail: '',
    reservationDate: new Date().toISOString().split('T')[0],
    expiryDate: '',
    depositAmount: '',
    notes: ''
  });

  useEffect(() => {
    loadReservations();
  }, []);

  const loadReservations = async () => {
    try {
      setLoading(true);

      // 1단계: 예약 데이터 로드
      const reservationsRef = collection(db, 'reservations');
      const q = query(reservationsRef, orderBy('reservationDate', 'desc'));
      const snapshot = await getDocs(q);

      // 2단계: 모든 productId 수집
      const productIds = [...new Set(snapshot.docs.map(doc => doc.data().productId).filter(Boolean))];

      // 3단계: 제품 정보를 한 번에 로드
      const productMap = {};
      if (productIds.length > 0) {
        const inventoryRef = collection(db, 'inventory');
        const inventorySnapshot = await getDocs(inventoryRef);
        inventorySnapshot.docs.forEach(doc => {
          if (productIds.includes(doc.id)) {
            productMap[doc.id] = {
              id: doc.id,
              ...doc.data()
            };
          }
        });
      }

      // 4단계: 예약 데이터와 제품 정보 결합
      const reservationList = snapshot.docs.map(docSnap => {
        const reservationData = docSnap.data();
        return {
          id: docSnap.id,
          ...reservationData,
          productInfo: reservationData.productId ? productMap[reservationData.productId] : null,
          reservationDate: reservationData.reservationDate?.toDate?.() || new Date(),
          expiryDate: reservationData.expiryDate?.toDate?.() || null,
          createdAt: reservationData.createdAt?.toDate?.() || new Date()
        };
      });

      setReservations(reservationList);
    } catch (error) {
      console.error('예약 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProduct) {
      alert('제품을 선택해주세요.');
      return;
    }

    try {
      // 예약 추가
      await addDoc(collection(db, 'reservations'), {
        productId: selectedProduct.id,
        productName: selectedProduct.productName,
        customerName: formData.customerName,
        customerContact: formData.customerContact,
        customerEmail: formData.customerEmail || null,
        reservationDate: new Date(formData.reservationDate),
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate) : null,
        depositAmount: formData.depositAmount ? parseInt(formData.depositAmount) : 0,
        notes: formData.notes,
        status: 'active',
        createdAt: serverTimestamp(),
        createdBy: user.email
      });

      // 제품 상태를 '예약'으로 변경
      const productRef = doc(db, 'inventory', selectedProduct.id);
      await updateDoc(productRef, {
        status: '예약',
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });

      // 이력 기록
      await addDoc(collection(db, 'inventory_history'), {
        productId: selectedProduct.id,
        changeType: '상태변경',
        previousValue: selectedProduct.status,
        newValue: '예약',
        timestamp: serverTimestamp(),
        userId: user.email
      });

      alert('예약이 등록되었습니다.');
      resetForm();
      loadReservations();
    } catch (error) {
      console.error('예약 등록 오류:', error);
      alert('예약 등록 중 오류가 발생했습니다.');
    }
  };

  const completeReservation = async (reservation) => {
    if (!window.confirm('이 예약을 완료 처리하시겠습니까?')) {
      return;
    }

    try {
      const reservationRef = doc(db, 'reservations', reservation.id);
      await updateDoc(reservationRef, {
        status: 'completed',
        completedAt: serverTimestamp(),
        completedBy: user.email
      });

      alert('예약이 완료되었습니다.');
      loadReservations();
    } catch (error) {
      console.error('예약 완료 오류:', error);
      alert('예약 완료 처리 중 오류가 발생했습니다.');
    }
  };

  const cancelReservation = async (reservation) => {
    const reason = window.prompt('취소 사유를 입력해주세요:');
    if (!reason) return;

    try {
      const reservationRef = doc(db, 'reservations', reservation.id);
      await updateDoc(reservationRef, {
        status: 'cancelled',
        cancelReason: reason,
        cancelledAt: serverTimestamp(),
        cancelledBy: user.email
      });

      // 제품 상태를 '판매가능'으로 변경
      if (reservation.productId) {
        const productRef = doc(db, 'inventory', reservation.productId);
        await updateDoc(productRef, {
          status: '판매가능',
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        });

        // 이력 기록
        await addDoc(collection(db, 'inventory_history'), {
          productId: reservation.productId,
          changeType: '상태변경',
          previousValue: '예약',
          newValue: '판매가능',
          timestamp: serverTimestamp(),
          userId: user.email
        });
      }

      alert('예약이 취소되었습니다.');
      loadReservations();
    } catch (error) {
      console.error('예약 취소 오류:', error);
      alert('예약 취소 중 오류가 발생했습니다.');
    }
  };

  const deleteReservation = async (reservation) => {
    if (!window.confirm('이 예약을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'reservations', reservation.id));
      alert('예약이 삭제되었습니다.');
      loadReservations();
    } catch (error) {
      console.error('예약 삭제 오류:', error);
      alert('예약 삭제 중 오류가 발생했습니다.');
    }
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      customerContact: '',
      customerEmail: '',
      reservationDate: new Date().toISOString().split('T')[0],
      expiryDate: '',
      depositAmount: '',
      notes: ''
    });
    setSelectedProduct(null);
    setShowAddModal(false);
  };

  const filteredReservations = reservations.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return '예약중';
      case 'completed':
        return '완료';
      case 'cancelled':
        return '취소';
      default:
        return status;
    }
  };

  const isExpired = (reservation) => {
    if (!reservation.expiryDate || reservation.status !== 'active') return false;
    return reservation.expiryDate < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">예약 관리</h2>
          <p className="text-gray-500 mt-1">총 <span className="font-bold text-brand-600">{filteredReservations.length}</span>건의 예약이 있습니다.</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2.5 rounded-lg transition shadow-md flex items-center font-medium"
        >
          <Icons.Plus className="w-5 h-5 mr-2" />
          예약 등록
        </button>
      </div>

      {/* 필터 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-4 mb-8">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-lg transition font-medium flex items-center ${filter === 'active'
                ? 'bg-brand-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            <Icons.Clock className="w-4 h-4 mr-2" />
            예약중 ({reservations.filter(r => r.status === 'active').length})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-4 py-2 rounded-lg transition font-medium flex items-center ${filter === 'completed'
                ? 'bg-brand-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            <Icons.Check className="w-4 h-4 mr-2" />
            완료 ({reservations.filter(r => r.status === 'completed').length})
          </button>
          <button
            onClick={() => setFilter('cancelled')}
            className={`px-4 py-2 rounded-lg transition font-medium flex items-center ${filter === 'cancelled'
                ? 'bg-brand-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            <Icons.X className="w-4 h-4 mr-2" />
            취소 ({reservations.filter(r => r.status === 'cancelled').length})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition font-medium flex items-center ${filter === 'all'
                ? 'bg-brand-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            <Icons.List className="w-4 h-4 mr-2" />
            전체 ({reservations.length})
          </button>
        </div>
      </div>

      {/* 예약 목록 */}
      <div className="space-y-6">
        {filteredReservations.map(reservation => (
          <div
            key={reservation.id}
            className={`bg-white rounded-xl shadow-sm border p-6 transition-all duration-300 hover:shadow-md ${isExpired(reservation) ? 'border-red-300 ring-2 ring-red-100' : 'border-brand-100'
              }`}
          >
            <div className="flex flex-col md:flex-row justify-between items-start gap-6 mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-xl font-bold text-brand-900 flex items-center">
                    <Icons.User className="w-5 h-5 mr-2 text-brand-500" />
                    {reservation.customerName}
                  </h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getStatusColor(reservation.status)}`}>
                    {getStatusText(reservation.status)}
                  </span>
                  {isExpired(reservation) && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                      <Icons.AlertCircle className="w-3 h-3 mr-1" />
                      기한 만료
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600 space-y-1.5 pl-7">
                  <div className="flex items-center">
                    <Icons.Phone className="w-4 h-4 mr-2 text-gray-400" />
                    {reservation.customerContact}
                  </div>
                  {reservation.customerEmail && (
                    <div className="flex items-center">
                      <Icons.Mail className="w-4 h-4 mr-2 text-gray-400" />
                      {reservation.customerEmail}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 w-full md:w-auto">
                {reservation.status === 'active' && (
                  <>
                    <button
                      onClick={() => completeReservation(reservation)}
                      className="flex-1 md:flex-none px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded-lg transition text-sm font-medium flex items-center justify-center"
                    >
                      <Icons.Check className="w-4 h-4 mr-2" />
                      완료
                    </button>
                    <button
                      onClick={() => cancelReservation(reservation)}
                      className="flex-1 md:flex-none px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg transition text-sm font-medium flex items-center justify-center"
                    >
                      <Icons.X className="w-4 h-4 mr-2" />
                      취소
                    </button>
                  </>
                )}

                {reservation.status !== 'active' && (
                  <button
                    onClick={() => deleteReservation(reservation)}
                    className="flex-1 md:flex-none px-4 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200 rounded-lg transition text-sm font-medium flex items-center justify-center"
                  >
                    <Icons.Trash className="w-4 h-4 mr-2" />
                    삭제
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <div className="space-y-3">
                <h4 className="font-bold text-brand-900 flex items-center text-sm uppercase tracking-wide">
                  <Icons.Box className="w-4 h-4 mr-2 text-brand-500" />
                  예약 제품
                </h4>
                {reservation.productInfo ? (
                  <Link
                    to={`/inventory/${reservation.productId}`}
                    className="block p-3 bg-white rounded-lg border border-gray-200 hover:border-brand-300 hover:shadow-sm transition group"
                  >
                    <div className="font-bold text-brand-800 group-hover:text-brand-600 transition-colors mb-1">
                      {reservation.productName}
                    </div>
                    <div className="text-sm text-gray-500 font-medium">
                      {reservation.productInfo.salePrice?.toLocaleString()}원
                    </div>
                  </Link>
                ) : (
                  <div className="p-3 bg-gray-100 rounded-lg border border-gray-200 text-gray-500 text-sm italic">
                    {reservation.productName} (삭제된 제품)
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <h4 className="font-bold text-brand-900 flex items-center text-sm uppercase tracking-wide">
                  <Icons.Calendar className="w-4 h-4 mr-2 text-brand-500" />
                  예약 정보
                </h4>
                <div className="text-sm space-y-2 bg-white p-3 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">예약일</span>
                    <span className="font-medium text-gray-900">
                      {reservation.reservationDate.toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                  {reservation.expiryDate && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">만료일</span>
                      <span className={`font-medium ${isExpired(reservation) ? 'text-red-600 font-bold' : 'text-gray-900'}`}>
                        {reservation.expiryDate.toLocaleDateString('ko-KR')}
                      </span>
                    </div>
                  )}
                  {reservation.depositAmount > 0 && (
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100 mt-2">
                      <span className="text-gray-500 font-medium">계약금</span>
                      <span className="font-bold text-brand-600">
                        {reservation.depositAmount.toLocaleString()}원
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {reservation.notes && (
              <div className="mt-4 flex items-start p-3 bg-yellow-50 rounded-lg border border-yellow-100 text-sm text-yellow-800">
                <Icons.FileText className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-yellow-600" />
                <div>
                  <span className="font-bold mr-1">메모:</span>
                  {reservation.notes}
                </div>
              </div>
            )}

            {reservation.status === 'cancelled' && reservation.cancelReason && (
              <div className="mt-4 flex items-start p-3 bg-red-50 rounded-lg border border-red-100 text-sm text-red-800">
                <Icons.AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-red-600" />
                <div>
                  <span className="font-bold mr-1">취소 사유:</span>
                  {reservation.cancelReason}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredReservations.length === 0 && (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
          <Icons.Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {filter === 'all' ? '예약 내역이 없습니다' : `${getStatusText(filter)} 예약이 없습니다`}
          </h3>
          <p className="text-gray-500 mb-6">새로운 예약을 등록하여 고객을 관리해보세요.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2.5 rounded-lg transition shadow-md font-medium"
          >
            첫 예약 등록하기
          </button>
        </div>
      )}

      {/* 예약 추가 모달 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all scale-100">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
                <h2 className="text-2xl font-bold text-brand-900 flex items-center">
                  <Icons.Plus className="w-6 h-6 mr-2 text-brand-500" />
                  새 예약 등록
                </h2>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                >
                  <Icons.X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <ProductSelector selectedProduct={selectedProduct} onSelect={setSelectedProduct} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-brand-900 mb-2">고객명 <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.customerName}
                      onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                      placeholder="고객 이름"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-brand-900 mb-2">연락처 <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      value={formData.customerContact}
                      onChange={(e) => setFormData({ ...formData, customerContact: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                      placeholder="010-0000-0000"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">이메일</label>
                  <input
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="example@email.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-brand-900 mb-2">예약일 <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={formData.reservationDate}
                      onChange={(e) => setFormData({ ...formData, reservationDate: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-brand-900 mb-2">만료일</label>
                    <input
                      type="date"
                      value={formData.expiryDate}
                      onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">계약금</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={formData.depositAmount}
                      onChange={(e) => setFormData({ ...formData, depositAmount: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                      placeholder="0"
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">원</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">메모</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="예약 관련 특이사항을 입력하세요"
                  />
                </div>

                <div className="flex space-x-3 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 px-6 py-3 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition font-medium"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition shadow-md font-bold flex items-center justify-center"
                  >
                    <Icons.Check className="w-5 h-5 mr-2" />
                    예약 등록
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 제품 선택 컴포넌트
function ProductSelector({ selectedProduct, onSelect }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    loadAvailableProducts();
  }, []);

  const loadAvailableProducts = async () => {
    try {
      const inventoryRef = collection(db, 'inventory');
      const q = query(inventoryRef, where('status', '==', '판매가능'));
      const snapshot = await getDocs(q);

      const productList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setProducts(productList);
    } catch (error) {
      console.error('제품 로드 오류:', error);
    }
  };

  const filteredProducts = products.filter(p =>
    p.productName?.toLowerCase().includes(search.toLowerCase()) ||
    p.barcodeId?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative">
      <label className="block text-sm font-bold text-brand-900 mb-2">예약 제품 <span className="text-red-500">*</span></label>
      {selectedProduct ? (
        <div className="p-4 bg-brand-50 rounded-lg border border-brand-200 flex justify-between items-center">
          <div>
            <div className="font-bold text-brand-900">{selectedProduct.productName}</div>
            <div className="text-sm text-brand-600 font-medium">{selectedProduct.salePrice?.toLocaleString()}원</div>
          </div>
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="px-3 py-1.5 bg-white border border-brand-200 text-brand-700 hover:bg-brand-100 rounded-lg text-sm font-medium transition-colors"
          >
            변경
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setShowList(true)}
              placeholder="제품명 또는 바코드로 검색..."
              className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
            />
            <Icons.Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
          {showList && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
              {filteredProducts.length > 0 ? (
                filteredProducts.map(product => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => {
                      onSelect(product);
                      setShowList(false);
                      setSearch('');
                    }}
                    className="w-full px-4 py-3 hover:bg-brand-50 text-left border-b border-gray-50 last:border-b-0 transition-colors"
                  >
                    <div className="font-bold text-gray-900">{product.productName}</div>
                    <div className="text-sm text-gray-500 flex justify-between mt-1">
                      <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">{product.category}</span>
                      <span className="font-medium text-brand-600">{product.salePrice?.toLocaleString()}원</span>
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-8 text-gray-500 text-center flex flex-col items-center">
                  <Icons.AlertCircle className="w-8 h-8 text-gray-300 mb-2" />
                  <p>판매 가능한 제품이 없습니다.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ReservationManagement;
